import { isNil } from 'lodash-es';
import { CircleCheckIcon, CoinsIcon, MegaphoneIcon, UsersIcon, ZapIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { CampaignSender } from '@/shared/types/campaigns';
import { IntegrationType } from '@/shared/types/integration';
import { getChannelDisplayName } from '@/util/channelName';

import { parseCampaignSendersChain } from '../../../utils';

interface ReviewSummaryProps {
  campaignType: string;
  subscriberCount: number;
  campaignSenders?: CampaignSender[];
  aiAgentName?: string;
  aiAgentVersion?: number;
  isNBA?: boolean;
}

export const ReviewSummary = ({
  campaignType,
  subscriberCount,
  campaignSenders,
  aiAgentName,
  aiAgentVersion,
  isNBA,
}: ReviewSummaryProps) => {
  const { t } = useTranslation();

  const { primarySender, deliveryFailedSender, noResponseSenders } =
    parseCampaignSendersChain(campaignSenders);

  const isPaymentRecovery = campaignType === 'payment_recovery';

  // Distinct channel names of the follow-up (non-primary) senders, used for the
  // agentic "Additional channels" row.
  const additionalChannels = [
    ...new Set(
      (campaignSenders ?? [])
        .slice(1)
        .map((sender) => sender.integration_type)
        .filter((type): type is string => !!type)
        .map((type) => getChannelDisplayName(type as IntegrationType))
    ),
  ];

  return (
    <div className="flex-1 p-6">
      <h3 className="mb-4 text-lg font-semibold">{t('campaigns.review.summary')}</h3>

      <div className="space-y-2">
        {/* Type */}
        <div className="grid grid-cols-2 items-center">
          <span className="text-sm text-muted-foreground">{t('campaigns.review.type_label')}</span>
          <div className="flex items-center gap-2">
            {isPaymentRecovery ? (
              <CoinsIcon size={16} className="text-muted-foreground" />
            ) : (
              <MegaphoneIcon size={16} className="text-muted-foreground" />
            )}
            <span className="font-medium">
              {isPaymentRecovery
                ? t('campaigns.setup.type_payment_recovery')
                : t('campaigns.setup.type_outreach')}
            </span>
          </div>
        </div>

        {/* Audience */}
        <div className="grid grid-cols-2 items-center">
          <span className="text-sm text-muted-foreground">{t('campaigns.review.audience')}</span>
          <div className="flex items-center gap-2">
            <UsersIcon size={16} className="text-muted-foreground" />
            <span className="font-medium">{subscriberCount}</span>
          </div>
        </div>

        {/* First channel */}
        <div className="grid grid-cols-2 items-center">
          <span className="text-sm text-muted-foreground">
            {t('campaigns.review.first_channel')}
          </span>
          <div className="flex items-center gap-2">
            {primarySender?.integration_type ? (
              <>
                <CircleCheckIcon size={16} className="text-muted-foreground" />
                <span className="font-medium">
                  {getChannelDisplayName(primarySender.integration_type as IntegrationType)}
                </span>
              </>
            ) : (
              <span className="text-sm text-muted-foreground">—</span>
            )}
          </div>
        </div>

        {/* If not delivered / Additional channels - depends on NBA */}
        {isNBA ? (
          // NBA: always show the additional channels row, with a "-" fallback when
          // there are none (mirrors the non-agentic "If not delivered" row).
          <div className="grid grid-cols-2 items-center">
            <span className="text-sm text-muted-foreground">
              {t('campaigns.review.additional_channels')}
            </span>
            <div className="flex items-center gap-2">
              {additionalChannels.length > 0 ? (
                <>
                  <CircleCheckIcon size={16} className="text-muted-foreground" />
                  <span className="font-medium">{additionalChannels.join(' · ')}</span>
                </>
              ) : (
                <span className="text-sm text-muted-foreground">-</span>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* If not delivered */}
            <div className="grid grid-cols-2 items-center">
              <span className="text-sm text-muted-foreground">
                {t('campaigns.review.if_not_delivered')}
              </span>
              <div className="flex items-center gap-2">
                {deliveryFailedSender?.integration_type ? (
                  <>
                    <CircleCheckIcon size={16} className="text-muted-foreground" />
                    <span className="font-medium">
                      {getChannelDisplayName(
                        deliveryFailedSender.integration_type as IntegrationType
                      )}
                    </span>
                  </>
                ) : (
                  <span className="text-sm text-muted-foreground">-</span>
                )}
              </div>
            </div>

            {/* If no response */}
            {noResponseSenders.length > 0 && (
              <div className="grid grid-cols-2 items-center">
                <span className="text-sm text-muted-foreground">
                  {t('campaigns.review.if_no_response')}
                </span>
                <div className="flex flex-col gap-1">
                  {noResponseSenders.map((item) => (
                    <div key={item.sender.sender_id} className="flex items-center gap-2">
                      <CircleCheckIcon size={16} className="text-muted-foreground" />
                      <span className="font-medium">
                        {getChannelDisplayName(item.sender.integration_type as IntegrationType)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Response handling */}
        <div className="grid grid-cols-2 items-center">
          <span className="text-sm text-muted-foreground">
            {t('campaigns.review.response_handling')}
          </span>
          <div className="flex items-center gap-2">
            <ZapIcon size={16} className="shrink-0 text-muted-foreground" />
            <span className="min-w-0 truncate text-sm leading-6 font-medium">
              {aiAgentName || '-'}
            </span>
            {!isNil(aiAgentVersion) && (
              <span className="shrink-0 text-sm leading-6">- v{aiAgentVersion}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
